import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiMonitor, FiServer, FiTool } from 'react-icons/fi';

const ServicesSection = styled.section`
  padding: 100px 0;
  background: #0a192f;
  min-height: 100vh;
  display: flex;
  align-items: center;
  perspective: 2000px;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 80px;
`;

const Title = styled.h2`
  font-size: 3rem;
  color: #ccd6f6;
  margin-bottom: 20px;
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: #64ffda;
    border-radius: 2px;
  }
`;

const Subtitle = styled.p`
  color: #8892b0;
  font-size: 1.2rem;
  max-width: 600px;
  margin: 0 auto;
`;

const ServicesGrid = styled.div`
  display: flex;
  justify-content: center;
  gap: 40px;
  margin-top: 50px;
  perspective: 1000px;
  flex-wrap: nowrap;
  padding: 20px 0;
  
  @media (max-width: 1200px) {
    flex-wrap: wrap;
  }
`;

const CardWrapper = styled(motion.div)`
  height: 400px;
  width: 350px;
  flex-shrink: 0;
  perspective: 1000px;
  cursor: pointer;
  
  @media (max-width: 1200px) {
    width: 300px;
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const CardInner = styled(motion.div)`
  position: relative;
  width: 100%;
  height: 100%;
  text-align: center;
  transform-style: preserve-3d;
`;

const CardFace = styled(motion.div)`
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  background: rgba(17, 34, 64, 0.7);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(100, 255, 218, 0.1);
  box-shadow: 0 10px 30px -15px rgba(2, 12, 27, 0.7);
`;

const CardFront = styled(CardFace)`
  transform: rotateY(0deg);
  background: linear-gradient(
    135deg,
    rgba(17, 34, 64, 0.9) 0%,
    rgba(17, 34, 64, 0.7) 100%
  );
`;

const CardBack = styled(CardFace)`
  transform: rotateY(180deg);
  background: linear-gradient(
    135deg,
    rgba(17, 34, 64, 0.95) 0%,
    rgba(17, 34, 64, 0.85) 100%
  );
`;

const ServiceIcon = styled.div`
  font-size: 3rem;
  color: #64ffda;
  margin-bottom: 1.5rem;
  
  svg {
    filter: drop-shadow(0 0 8px rgba(100, 255, 218, 0.3));
  }
`;

const ServiceTitle = styled.h3`
  color: #e6f1ff;
  font-size: 1.75rem;
  margin: 1rem 0;
  font-weight: 600;
`;

const ServiceTagline = styled.p`
  color: #64ffda;
  font-size: 1.1rem;
  margin-top: 0.5rem;
  font-style: italic;
`;

const ServiceDescription = styled.p`
  color: #8892b0;
  line-height: 1.8;
  font-size: 1rem;
  margin-bottom: 1.5rem;
`;

const ServiceFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0;
  text-align: left;
`;

const ServiceFeature = styled.li`
  color: #8892b0;
  margin: 0.75rem 0;
  display: flex;
  align-items: center;
  font-size: 0.95rem;
  
  &:before {
    content: '▹';
    color: #64ffda;
    margin-right: 10px;
  }
`;

const LearnMore = styled.button`
  background: transparent;
  border: 1px solid #64ffda;
  color: #64ffda;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: auto;
  
  &:hover {
    background: rgba(100, 255, 218, 0.1);
  }
`;

const Services = () => {
  const [flippedCards, setFlippedCards] = useState({});

  const services = [
    {
      icon: <FiMonitor />,
      title: "Développement Frontend",
      tagline: "Des interfaces modernes et réactives",
      description: "Création d'expériences utilisateur exceptionnelles avec les dernières technologies web.",
      features: [
        "Applications React performantes",
        "Design responsive et adaptatif",
        "Animations fluides et interactives",
        "Optimisation UX/UI"
      ]
    },
    {
      icon: <FiServer />,
      title: "Développement Backend",
      tagline: "Des APIs robustes et évolutives",
      description: "Architecture backend solide pour supporter vos applications web.",
      features: [
        "APIs RESTful avec Node.js",
        "Gestion de bases de données MongoDB",
        "Sécurité et authentification",
        "Architecture scalable"
      ]
    },
    {
      icon: <FiTool />,
      title: "Conseil Technique",
      tagline: "Expertise et accompagnement",
      description: "Conseils stratégiques pour optimiser votre développement.",
      features: [
        "Choix d'architecture",
        "Bonnes pratiques de développement",
        "Déploiement et maintenance",
        "Ai tips and tricks"
      ]
    }
  ];

  const flipCard = (index) => {
    setFlippedCards(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  return (
    <ServicesSection id="services">
      <Container>
        <SectionHeader>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Title>Mes Services</Title>
            <Subtitle>Des solutions sur mesure pour vos projets web</Subtitle>
          </motion.div>
        </SectionHeader>

        <ServicesGrid>
          {services.map((service, index) => (
            <CardWrapper
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
              onClick={() => flipCard(index)}
            >
              <CardInner
                animate={{ rotateY: flippedCards[index] ? 180 : 0 }}
                transition={{ duration: 0.6, type: "tween", ease: "easeInOut" }}
              >
                <CardFront>
                  <ServiceIcon>{service.icon}</ServiceIcon>
                  <ServiceTitle>{service.title}</ServiceTitle>
                  <ServiceTagline>{service.tagline}</ServiceTagline>
                  <LearnMore>En savoir plus</LearnMore>
                </CardFront>
                
                <CardBack>
                  <ServiceTitle>{service.title}</ServiceTitle>
                  <ServiceDescription>{service.description}</ServiceDescription>
                  <ServiceFeatures>
                    {service.features.map((feature, i) => (
                      <ServiceFeature key={i}>{feature}</ServiceFeature>
                    ))}
                  </ServiceFeatures>
                  <LearnMore>Retour</LearnMore>
                </CardBack>
              </CardInner>
            </CardWrapper>
          ))}
        </ServicesGrid>
      </Container>
    </ServicesSection>
  );
};

export default Services;
